import React, { useState, useEffect, useCallback } from 'react';
import { Grid, Typography, MenuItem, TextField } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { InstrumentoFinanceiro, Perfil } from 'common/genericTypes';
import { GetApp, MonetizationOn } from '@material-ui/icons';
import { RootState } from 'store';
import { componentsPropsValues, localizedTextsMap, rowsPerPageOptionsValues } from 'utils/dataGrid/dataGridUtils';
import CustomToolBarUtils from 'utils/dataGrid/customToolBar';
import { MdSpace, MoreOptionsMenu } from 'm2m.components';
import { GenericDrawer } from 'components/genericDrawer';
import { GenericToast, GenericToastKind } from 'components/genericToast';
import {
  DataGridPro,
  GridCellParams,
  GridValueGetterParams,
  GridSortModel,
  GridFilterModel} from '@mui/x-data-grid-pro';
import { addCpfCnpjMask } from 'utils/mask';
import { formatDate } from 'utils/date';

// @ts-ignore
import * as html2pdf from 'html2pdf.js';
import { LiquidacaoComprovanteDTO, TiposOperacaoEnum } from 'requests/liquidacoes';
import { getInstrumentoFinanceiroLongDescription } from 'common/genericDescriptions';

export enum LancamentoStatus {
  PENDENTE = 'PENDENTE',
  PARA_LIQUIDAR = 'PARA_LIQUIDAR',
  LIQUIDADO = 'LIQUIDADO',
  CANCELADO = 'CANCELADO',
  PREVISTO = 'PREVISTO',
}

// Helper function to get status display text
const getLancamentoStatusText = (status: LancamentoStatus): string => {
  switch (status) {
    case LancamentoStatus.PENDENTE:
      return 'Pendente';
    case LancamentoStatus.PARA_LIQUIDAR:
      return 'Para Liquidar';
    case LancamentoStatus.LIQUIDADO:
      return 'Liquidado';
    case LancamentoStatus.CANCELADO:
      return 'Cancelado';
    case LancamentoStatus.PREVISTO:
      return 'Previsto';
    default:
      return 'Desconhecido';
  }
};

export interface LiquidacaoData {
  idLancamento: string;
  iGrupo: string;
  idSolicitacao: string;
  dataOperacao: string | Date;
  tipoAtivo: InstrumentoFinanceiro;
  codigo: string;
  emissor: string;
  cnpjEmissor: string;
  quantidade: number;
  precoUnitario: number;
  precoTotal: number;
  tipoOperacao: string;
  pontaOperacao: TiposOperacaoEnum;
  investidor: string;
  cnpjInvestidor: string;
  bancoOrigem: string;
  bancoDestino: string;
  status: LancamentoStatus;
  dataLiquidacao: string | Date;
  modalidade: string;
  comprovante: string | null;
}

const Liquidacoes: React.FC = () => {
  const dispatch = useDispatch();
  const postComprovanteApi = useSelector((state: RootState) => state.liquidacoesApi.postComprovante);
  const getLiquidacoesApi = useSelector((state: RootState) => state.liquidacoesApi.getLiquidacoes);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [pageCount, setPageCount] = useState<number>(0);
  const [totalItens, setTotalItens] = useState<number>(0);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [liquidacoes, setLiquidacoes] = useState<LiquidacaoData[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [openLiquidarDrawer, setOpenLiquidarDrawer] = useState<boolean>(false);
  const [selectedLiquidacao, setSelectedLiquidacao] = useState<LiquidacaoData | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [liquidacaoRealizada, setLiquidacaoRealizada] = useState<LiquidacaoData | null>(null);
  const [filterDataLiquidacao, setFilterDataLiquidacao] = useState<string>('');
  const [filterSituacao, setFilterSituacao] = useState<LancamentoStatus | ''>('');
  const [filterCodigoIF, setFilterCodigoIF] = useState<string>('');
  const isUsuarioLeitura = useSelector((state: RootState) => state.auth.usuario.participante.perfil === Perfil.Leitura);

  const getLiquidacoes = useCallback(async () => {
    try {
      setShowLoader(true);

      const queryParams = new URLSearchParams();
      queryParams.append('Page', (page + 1).toString());
      queryParams.append('PageSize', pageSize.toString());
      if (filterDataLiquidacao) {
        queryParams.append('DataLancamentos', filterDataLiquidacao);
      }
      if (filterSituacao !== '') {
        queryParams.append('Status', filterSituacao.toString());
      }
      if (filterCodigoIF) {
        queryParams.append('Codigo', filterCodigoIF);
      }

      const result = await getLiquidacoesApi(dispatch, queryParams.toString()) as any;
      setLiquidacoes(result.data);
      setPageCount(Math.ceil(result.totalCount / pageSize));
      setTotalItens(result.pagination.total);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setShowLoader(false);
    }
  }, [page, pageSize, sortModel, filterDataLiquidacao, filterSituacao, filterCodigoIF]);

  useEffect(() => {
    getLiquidacoes();
  }, [getLiquidacoes]);

  const onSortChange = useCallback(
    (sortModel: GridSortModel) => {
      setSortModel(sortModel);
      setPage(0);
    },
    []
  );

  const handleDownload = async (liquidacao: LiquidacaoData) => {
    try {
      const payload: LiquidacaoComprovanteDTO = {
        ...liquidacao,
        dataOperacao: new Date(liquidacao.dataOperacao),
        tipoInstrumentoFinanceiro: liquidacao.tipoAtivo,
        codigo: liquidacao.codigo,
        codigoOperacao: liquidacao.tipoOperacao,
        codigoExterno: '00000000-0000-0000-0000-000000000000',
        cpfCnpjEmissor: liquidacao.cnpjEmissor,
        precoNominalUnitario: liquidacao.precoUnitario,
        codigoOperacaoExterno: liquidacao.pontaOperacao,
        cpfCnpjInvestidor: liquidacao.cnpjInvestidor,
        dataLiquidacao: liquidacao.dataLiquidacao ? new Date(liquidacao.dataLiquidacao) : null,
        situacao: liquidacao.status,
      };

      const htmlContent = await postComprovanteApi(dispatch, payload);
      
      if (!htmlContent) {
        alert('Não foi possível gerar o PDF para esse comprovante.');
        return;
      }

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;

      const logo = await fetch('https://laqus-static-prod.s3.sa-east-1.amazonaws.com/assets/laqus.png');
      const logoBlob = await logo.blob();
      const reader = new FileReader();
      
      const base64Logo = await new Promise<string>((resolve) => {
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(logoBlob);
      });

      const imgElement = tempDiv.querySelector('img[src="https://laqus-static-prod.s3.sa-east-1.amazonaws.com/assets/laqus.png"]') as HTMLImageElement;
      if (imgElement) {
        imgElement.src = base64Logo;
      }

      const options = {
        margin: 1,
        filename: 'comprovante_liquidacao.pdf',
        image: { type: 'jpeg', quality: 0.75 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };

      html2pdf().from(tempDiv).set(options).save();
    } catch (err) {
      console.error(err);
      alert('Não foi possível gerar o PDF do Comprovante.');
    }
  };

  const handleLiquidar = useCallback(async () => {
    if (!selectedLiquidacao) return;
    setSubmitting(true);
    try {
      setLiquidacaoRealizada(selectedLiquidacao);
      setOpenLiquidarDrawer(false);
      await getLiquidacoes();
    } catch (error) {
      console.error('Erro ao liquidar:', error);
    } finally {
      setSubmitting(false);
    }
  }, [selectedLiquidacao]);

  const handleLiquidarDrawerClose = useCallback(() => {
    setOpenLiquidarDrawer(false);
    setSelectedLiquidacao(null);
  }, []);

  const handleLiquidarClick = useCallback((liquidacao: LiquidacaoData) => {
    setSelectedLiquidacao(liquidacao);
    setOpenLiquidarDrawer(true);
  }, []);

  const getGridColumns = useCallback(() => [
    {
      field: 'dataOperacao',
      headerName: 'Data da Operação',
      type: 'date',
      width: 130,
      minWidth: 120,
      maxWidth: 150,
      filterable: false,
      sortable: true,
      disableExport: false,
      valueGetter: (params: GridValueGetterParams) => (params.row as LiquidacaoData).dataOperacao,
      renderCell: (params: GridCellParams) => {
        const liquidacao = params.row as LiquidacaoData;
        const date = typeof liquidacao.dataOperacao === 'string' 
          ? new Date(liquidacao.dataOperacao)
          : liquidacao.dataOperacao;
        return (
          <Typography variant="body2">
            {formatDate(date)}
          </Typography>
        );
      },
    },
    {
      field: 'tipoAtivo',
      headerName: 'Tipo IF',
      width: 120,
      minWidth: 120,
      maxWidth: 150,
      filterable: false,
      sortable: true,
      disableExport: false,
      valueGetter: (params: GridValueGetterParams) => (params.row as LiquidacaoData).tipoAtivo,
      renderCell: (params: GridCellParams) => {
        const liquidacao = params.row as LiquidacaoData;
        return (
          <Typography variant="body2">
            {getInstrumentoFinanceiroLongDescription[liquidacao.tipoAtivo]}
          </Typography>
        );
      },
    },
    {
      field: 'codigo',
      headerName: 'Código IF',
      width: 130,
      minWidth: 120,
      maxWidth: 150,
      filterable: false,
      sortable: true,
      disableExport: false,
      valueGetter: (params: GridValueGetterParams) => (params.row as LiquidacaoData).codigo,
      renderCell: (params: GridCellParams) => {
        const liquidacao = params.row as LiquidacaoData;
        return (
          <Typography variant="body2">
            {liquidacao.codigo}
          </Typography>
        );
      },
    },
    {
      field: 'emissor',
      headerName: 'Emissor',
      width: 200,
      minWidth: 150,
      maxWidth: 250,
      filterable: false,
      sortable: true,
      disableExport: false,
      valueGetter: (params: GridValueGetterParams) => (params.row as LiquidacaoData).emissor,
      renderCell: (params: GridCellParams) => {
        const liquidacao = params.row as LiquidacaoData;
        return (
          <Typography variant="body2" style={{ whiteSpace: 'normal' }}>
            {liquidacao.emissor}
          </Typography>
        );
      },
    },
    {
      field: 'cnpjEmissor',
      headerName: 'CNPJ Emissor',
      width: 150,
      minWidth: 130,
      maxWidth: 170,
      filterable: false,
      sortable: true,
      disableExport: false,
      valueGetter: (params: GridValueGetterParams) => (params.row as LiquidacaoData).cnpjEmissor,
      renderCell: (params: GridCellParams) => {
        const liquidacao = params.row as LiquidacaoData;
        return (
          <Typography variant="body2">
            {addCpfCnpjMask(liquidacao.cnpjEmissor)}
          </Typography>
        );
      },
    },
    {
      field: 'quantidade',
      headerName: 'Quantidade',
      type: 'number',
      width: 100,
      minWidth: 90,
      maxWidth: 120,
      filterable: false,
      sortable: true,
      disableExport: false,
      valueGetter: (params: GridValueGetterParams) => (params.row as LiquidacaoData).quantidade,
      renderCell: (params: GridCellParams) => {
        const liquidacao = params.row as LiquidacaoData;
        return (
          <Typography variant="body2">
            {liquidacao.quantidade?.toLocaleString('pt-BR')}
          </Typography>
        );
      },
    },
    {
      field: 'precoUnitario',
      headerName: 'PU',
      type: 'number',
      width: 120,
      minWidth: 100,
      maxWidth: 140,
      filterable: false,
      sortable: true,
      disableExport: false,
      valueGetter: (params: GridValueGetterParams) => (params.row as LiquidacaoData).precoUnitario,
      renderCell: (params: GridCellParams) => {
        const liquidacao = params.row as LiquidacaoData;
        return (
          <Typography variant="body2">
            {liquidacao.precoUnitario ? `R$ ${liquidacao.precoUnitario.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : ''}
          </Typography>
        );
      },
    },
    {
      field: 'precoTotal',
      headerName: 'Valor',
      type: 'number',
      width: 120,
      minWidth: 100,
      maxWidth: 140,
      filterable: false,
      sortable: true,
      disableExport: false,
      valueGetter: (params: GridValueGetterParams) => (params.row as LiquidacaoData).precoTotal,
      renderCell: (params: GridCellParams) => {
        const liquidacao = params.row as LiquidacaoData;
        return (
          <Typography variant="body2">
            {liquidacao.precoTotal ? `R$ ${liquidacao.precoTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : ''}
          </Typography>
        );
      },
    },
    {
      field: 'tipoOperacao',
      headerName: 'Tipo de Operação',
      width: 140,
      minWidth: 120,
      maxWidth: 160,
      filterable: false,
      sortable: true,
      disableExport: false,
      valueGetter: (params: GridValueGetterParams) => (params.row as LiquidacaoData).tipoOperacao,
      renderCell: (params: GridCellParams) => {
        const liquidacao = params.row as LiquidacaoData;
        return (
          <Typography variant="body2">
            {liquidacao.tipoOperacao}
          </Typography>
        );
      },
    },
    {
      field: 'pontaOperacao',
      headerName: 'Ponta na Operação',
      width: 140,
      minWidth: 120,
      maxWidth: 160,
      filterable: false,
      sortable: true,
      disableExport: false,
      valueGetter: (params: GridValueGetterParams) => (params.row as LiquidacaoData).pontaOperacao,
      renderCell: (params: GridCellParams) => {
        const liquidacao = params.row as LiquidacaoData;
        return (
          <Typography variant="body2">
            {TiposOperacaoEnum[liquidacao.pontaOperacao]?.toString()?.replaceAll("_", " ")} - {liquidacao.pontaOperacao}
          </Typography>
        );
      },
    },
    {
      field: 'investidor',
      headerName: 'Investidor',
      width: 200,
      minWidth: 150,
      maxWidth: 250,
      filterable: false,
      sortable: true,
      disableExport: false,
      valueGetter: (params: GridValueGetterParams) => (params.row as LiquidacaoData).investidor,
      renderCell: (params: GridCellParams) => {
        const liquidacao = params.row as LiquidacaoData;
        return (
          <Typography variant="body2" style={{ whiteSpace: 'normal' }}>
            {liquidacao.investidor}
          </Typography>
        );
      },
    },
    {
      field: 'cnpjInvestidor',
      headerName: 'CNPJ Investidor',
      width: 150,
      minWidth: 130,
      maxWidth: 170,
      filterable: false,
      sortable: true,
      disableExport: false,
      valueGetter: (params: GridValueGetterParams) => (params.row as LiquidacaoData).cnpjInvestidor,
      renderCell: (params: GridCellParams) => {
        const liquidacao = params.row as LiquidacaoData;
        return (
          <Typography variant="body2">
            {addCpfCnpjMask(liquidacao.cnpjInvestidor)}
          </Typography>
        );
      },
    },
    {
      field: 'bancoOrigem',
      headerName: 'Banco Origem',
      width: 130,
      minWidth: 120,
      maxWidth: 150,
      filterable: false,
      sortable: true,
      disableExport: false,
      valueGetter: (params: GridValueGetterParams) => (params.row as LiquidacaoData).bancoOrigem,
      renderCell: (params: GridCellParams) => {
        const liquidacao = params.row as LiquidacaoData;
        return (
          <Typography variant="body2">
            {liquidacao.bancoOrigem}
          </Typography>
        );
      },
    },
    {
      field: 'bancoDestino',
      headerName: 'Banco Destino',
      width: 130,
      minWidth: 120,
      maxWidth: 150,
      filterable: false,
      sortable: true,
      disableExport: false,
      valueGetter: (params: GridValueGetterParams) => (params.row as LiquidacaoData).bancoDestino,
      renderCell: (params: GridCellParams) => {
        const liquidacao = params.row as LiquidacaoData;
        return (
          <Typography variant="body2">
            {liquidacao.bancoDestino}
          </Typography>
        );
      },
    },
    {
      field: 'situacao',
      headerName: 'Situação',
      width: 120,
      minWidth: 100,
      maxWidth: 140,
      filterable: false,
      sortable: true,
      disableExport: false,
      valueGetter: (params: GridValueGetterParams) => (params.row as LiquidacaoData).status,
      renderCell: (params: GridCellParams) => {
        const liquidacao = params.row as LiquidacaoData;
        return (
          <Typography variant="body2">
            {getLancamentoStatusText(liquidacao.status)}
          </Typography>
        );
      },
    },
    {
      field: 'dataLiquidacao',
      headerName: 'Data da Liquidação',
      type: 'date',
      width: 140,
      minWidth: 130,
      maxWidth: 160,
      filterable: false,
      sortable: true,
      disableExport: false,
      valueGetter: (params: GridValueGetterParams) => (params.row as LiquidacaoData).dataLiquidacao,
      renderCell: (params: GridCellParams) => {
        const liquidacao = params.row as LiquidacaoData;
        const date = typeof liquidacao.dataLiquidacao === 'string' 
          ? new Date(liquidacao.dataLiquidacao)
          : liquidacao.dataLiquidacao;
        return (
          <Typography variant="body2">
            {formatDate(date)}
          </Typography>
        );
      },
    },
    {
      field: 'modalidade',
      headerName: 'Modalidade',
      width: 120,
      minWidth: 100,
      maxWidth: 140,
      filterable: false,
      sortable: false,
      disableExport: false,
      valueGetter: (params: GridValueGetterParams) => (params.row as LiquidacaoData).modalidade,
      renderCell: (params: GridCellParams) => {
        const liquidacao = params.row as LiquidacaoData;
        return (
          <Typography variant="body2">
            {liquidacao.modalidade}
          </Typography>
        );
      },
    },
    {
      field: 'comprovante',
      headerName: 'Comprovante',
      width: 100,
      minWidth: 90,
      maxWidth: 120,
      filterable: false,
      sortable: false,
      disableExport: true,
      valueGetter: (params: GridValueGetterParams) => (params.row as LiquidacaoData).comprovante,
      renderCell: (params: GridCellParams) => {
        const liquidacao = params.row as LiquidacaoData;
        return (
          liquidacao.status === LancamentoStatus.LIQUIDADO ? (
            <GetApp 
              style={{ cursor: 'pointer', color: '#1976d2' }} 
              onClick={() => handleDownload(liquidacao!)}
            />
          ) : null
        );
      },
    },
    {
      field: 'actions',
      headerName: ' ',
      filterable: false,
      sortable: false,
      disableExport: true,
      width: 100,
      minWidth: 90,
      maxWidth: 120,
      valueGetter: (params: GridValueGetterParams) => params.row,
      renderCell: (params: GridCellParams) => {
        const liquidacao = params.row as LiquidacaoData;
        if (isUsuarioLeitura) {
          return <></>;
        }
        return (
          <>
            <MoreOptionsMenu>
              <MenuItem
                onClick={() => handleLiquidarClick(liquidacao)}
                disabled={
                  getLancamentoStatusText(liquidacao.status) !== 'Cancelado'
                }
              >
                <MonetizationOn fontSize="small" style={{ marginRight: '10px' }} />
                {'Liquidar'}
              </MenuItem>
            </MoreOptionsMenu>
          </>
        );
      },
    },
  ], [handleDownload, handleLiquidarClick]);

  return (
    <div>
      <MdSpace />
      <Typography variant="h1">{'Liquidações'}</Typography>
      <MdSpace />
      
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <Typography>{'Data de Liquidação'}</Typography>
        </Grid>
        <Grid item>
          <TextField
            type="date"
            value={filterDataLiquidacao}
            onChange={(e) => {
              setFilterDataLiquidacao(e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
            size="small"
          />
        </Grid>
        <Grid item>
          <Typography>{'Situação'}</Typography>
        </Grid>
        <Grid item>
          <TextField
            select
            value={filterSituacao}
            onChange={(e) => {
              const value = e.target.value;
              setFilterSituacao(value === '' ? '' : value as LancamentoStatus);
            }}
            size="small"
            style={{ minWidth: 120 }}
          >
            <MenuItem value="">{'Todas'}</MenuItem>
            <MenuItem value={LancamentoStatus.PENDENTE}>{getLancamentoStatusText(LancamentoStatus.PENDENTE)}</MenuItem>
            <MenuItem value={LancamentoStatus.PARA_LIQUIDAR}>{getLancamentoStatusText(LancamentoStatus.PARA_LIQUIDAR)}</MenuItem>
            <MenuItem value={LancamentoStatus.LIQUIDADO}>{getLancamentoStatusText(LancamentoStatus.LIQUIDADO)}</MenuItem>
            <MenuItem value={LancamentoStatus.CANCELADO}>{getLancamentoStatusText(LancamentoStatus.CANCELADO)}</MenuItem>
            <MenuItem value={LancamentoStatus.PREVISTO}>{getLancamentoStatusText(LancamentoStatus.PREVISTO)}</MenuItem>
          </TextField>
        </Grid>
        <Grid item>
          <Typography>{'Código IF'}</Typography>
        </Grid>
        <Grid item>
          <TextField
            value={filterCodigoIF}
            onChange={(e) => {
              setFilterCodigoIF(e.target.value);
            }}
            size="small"
            placeholder="Digite o código IF"
          />
        </Grid>
      </Grid>
      <MdSpace />
      
      {liquidacaoRealizada && (
        <GenericToast
          kind={GenericToastKind.Success}
          onDismiss={() => setLiquidacaoRealizada(null)}
        >
          <Typography variant="body2" display="inline">
            {'Operação'}
          </Typography>
          &nbsp;
          <Typography display="inline">
            {`${liquidacaoRealizada.codigo} liquidada`}
          </Typography>
          &nbsp;
          <Typography variant="body2" display="inline">
            {'com sucesso'}
          </Typography>
        </GenericToast>
      )}
      
      <MdSpace />
      
      <DataGridPro
        autoHeight
        getRowId={(row) => row.idLancamento}
        localeText={localizedTextsMap}
        componentsProps={componentsPropsValues}
        rows={liquidacoes}
        columns={getGridColumns()}
        components={{
          Toolbar: CustomToolBarUtils,
        }}
        disableSelectionOnClick
        rowsPerPageOptions={rowsPerPageOptionsValues}
        page={page}
        rowCount={totalItens}
        pagination
        paginationMode="server"
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setPage(0);
        }}
        pageSize={pageSize}
        loading={showLoader}
        filterMode="server"
        sortingMode="server"
        onSortModelChange={onSortChange}
      />
      <MdSpace />
      <GenericDrawer
        opened={openLiquidarDrawer}
        title="Liquidar Operação"
        textContents={[
          {
            text: `Tem certeza que deseja liquidar a operação ${selectedLiquidacao?.tipoOperacao} do código ${selectedLiquidacao?.codigo}, no valor de ${selectedLiquidacao?.precoTotal?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} com o PU de ${selectedLiquidacao?.precoUnitario?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}?`
          }
        ]}
        onClose={handleLiquidarDrawerClose}
        buttons={[
          {
            buttonText: 'Liquidar',
            buttonAction: handleLiquidar,
            buttonColor: 'primary',
          },
          {
            buttonText: 'Cancelar',
            buttonAction: handleLiquidarDrawerClose,
          },
        ]}
        disableButtons={submitting}
      />
    </div>
  );
};

export default Liquidacoes;